import React, { useEffect, useState } from "react";
import { Space, Switch, Table, Tag, Typography, message } from "antd";
import { getErrorMessage } from "@/utilities";
import cardIcon from "@/components/icon/payment/card.png";
import getnetIcon from "@/components/icon/payment/getnet.png";
import mercadopagoIcon from "@/components/icon/payment/mercadopago.png";
import {
  getFiserv,
  getGetnet,
  getMercadoPago,
  upsertFiserv,
  upsertGetnet,
  upsertMercadoPago,
} from "./api";
import { GatewayPanelProps } from "./GatewayPanel";
import { FiservPanel } from "./FiservPanel";
import { ENCABEZADO_DE_TABLA, PAGINADOR } from "./ui";
import { MercadoPagoPanel } from "./MercadoPagoPanel";
import { GetnetPanel } from "./GetnetPanel";

const { Text } = Typography;

type GatewayKey = "fiserv" | "mercadopago" | "getnet";

/** Lo que el listado necesita de cualquier pasarela: el resto lo ve su panel. */
interface GatewayConfig {
  enabled: boolean;
  title?: string;
  productionModeEnabled?: boolean;
}

interface Gateway {
  key: GatewayKey;
  name: string;
  detail?: string;
  /** El mismo que muestra el detalle del presupuesto para el método de pago. */
  icon?: string;
  /** Mercado Pago no tiene modo: el entorno lo dan sus credenciales. */
  hasMode: boolean;
  load: (sellerCode: string) => Promise<GatewayConfig>;
  save: (sellerCode: string, config: any) => Promise<unknown>;
  Panel: React.FC<GatewayPanelProps>;
}

const GATEWAYS: Gateway[] = [
  {
    key: "fiserv",
    name: "Fiserv",
    icon: cardIcon,
    hasMode: true,
    load: getFiserv,
    save: upsertFiserv,
    Panel: FiservPanel,
  },
  {
    key: "mercadopago",
    name: "Mercado Pago",
    icon: mercadopagoIcon,
    hasMode: false,
    load: getMercadoPago,
    save: upsertMercadoPago,
    Panel: MercadoPagoPanel,
  },
  {
    key: "getnet",
    name: "Getnet",
    icon: getnetIcon,
    hasMode: true,
    load: getGetnet,
    save: upsertGetnet,
    Panel: GetnetPanel,
  },
];

interface Row {
  gateway: Gateway;
  /** `null` = no se pudo cargar: no se puede prender ni apagar desde el listado. */
  config: GatewayConfig | null;
}

interface Props {
  sellerCode: string;
}

/**
 * Las pasarelas del seller, como el listado de Offline (xcons-docs#636): el estado y el modo a la
 * vista, y la configuración en el panel que se abre con un clic en la fila.
 */
export const OnlinePaymentMethods: React.FC<Props> = ({ sellerCode }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<GatewayKey | null>(null);

  const load = async () => {
    setLoading(true);
    const results = await Promise.allSettled(
      GATEWAYS.map((g) => g.load(sellerCode)),
    );
    setRows(
      GATEWAYS.map((gateway, i) => {
        const r = results[i];
        return { gateway, config: r.status === "fulfilled" ? r.value : null };
      }),
    );
    // Un solo aviso aunque fallen las tres.
    const failed = GATEWAYS.filter((_, i) => results[i].status === "rejected");
    if (failed.length) {
      message.error(
        `No se pudo cargar ${failed.map((g) => g.name).join(", ")}`,
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    if (sellerCode) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerCode]);

  const toggleEnabled = async (row: Row, enabled: boolean) => {
    try {
      await row.gateway.save(sellerCode, { ...row.config, enabled });
      setRows((prev) =>
        prev.map((r) =>
          r.gateway.key === row.gateway.key
            ? { ...r, config: { ...r.config!, enabled } }
            : r,
        ),
      );
    } catch (err: any) {
      message.error(getErrorMessage(err));
    }
  };

  return (
    <>
      <Table<Row>
        rowKey={(r) => r.gateway.key}
        loading={loading}
        dataSource={rows}
        pagination={PAGINADOR}
        className="tabla-con-borde-redondeado"
        bordered
        size="small"
        components={ENCABEZADO_DE_TABLA}
        onRow={(r) => ({
          style: { cursor: "pointer" },
          onClick: () => setEditing(r.gateway.key),
        })}
        columns={[
          {
            title: "Pasarela",
            render: (_, r) => (
              <Space>
                {r.gateway.icon && (
                  <img
                    src={r.gateway.icon}
                    alt=""
                    style={{ width: 24, height: 24, objectFit: "contain" }}
                  />
                )}
                <div>
                  <div>{r.gateway.name}</div>
                  {r.gateway.detail && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {r.gateway.detail}
                    </Text>
                  )}
                </div>
              </Space>
            ),
          },
          {
            title: "Título en el checkout",
            render: (_, r) =>
              r.config?.title || <Text type="secondary">—</Text>,
          },
          {
            title: "Modo",
            width: 120,
            render: (_, r) =>
              r.gateway.hasMode && r.config ? (
                r.config.productionModeEnabled ? (
                  <Tag color="green">Producción</Tag>
                ) : (
                  <Tag color="gold">Prueba</Tag>
                )
              ) : (
                <Text type="secondary">—</Text>
              ),
          },
          {
            title: "Estado",
            width: 90,
            render: (_, r) =>
              r.config ? (
                <Switch
                  checked={r.config.enabled}
                  onChange={(v) => toggleEnabled(r, v)}
                  onClick={(_, e) => e.stopPropagation()}
                  size="small"
                />
              ) : (
                <Tag color="error">No se pudo cargar</Tag>
              ),
          },
        ]}
      />

      {GATEWAYS.map(({ key, Panel }) => (
        <Panel
          key={key}
          sellerCode={sellerCode}
          open={editing === key}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      ))}
    </>
  );
};
